import { cn } from "@/lib/utils";
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

export interface SplitTextProps {
    text: string;
    className?: string;
    delay?: number;
    duration?: number;
    ease?: string;
    splitType?: "chars" | "words" | "lines" | "words, chars";
    from?: {
        opacity?: number;
        y?: number;
        x?: number;
        scale?: number;
        rotateX?: number;
        rotateY?: number;
        rotateZ?: number;
    };
    to?: {
        opacity?: number;
        y?: number;
        x?: number;
        scale?: number;
        rotateX?: number;
        rotateY?: number;
        rotateZ?: number;
    };
    threshold?: number;
    rootMargin?: string;
    tag?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span";
    textAlign?: React.CSSProperties["textAlign"];
    onLetterAnimationComplete?: () => void;
}

interface AnimatedElementProps {
    delay: number;
    duration: number;
    ease: string;
    from: Required<NonNullable<SplitTextProps["from"]>>;
    to: Required<NonNullable<SplitTextProps["to"]>>;
    isVisible: boolean;
}

const getTransformValue = (
    props: Partial<Required<NonNullable<SplitTextProps["from"]>>>
) => {
    const transforms = [];
    if (props.x !== undefined) transforms.push(`translateX(${props.x}px)`);
    if (props.y !== undefined) transforms.push(`translateY(${props.y}px)`);
    if (props.scale !== undefined) transforms.push(`scale(${props.scale})`);
    if (props.rotateX !== undefined)
        transforms.push(`rotateX(${props.rotateX}deg)`);
    if (props.rotateY !== undefined)
        transforms.push(`rotateY(${props.rotateY}deg)`);
    if (props.rotateZ !== undefined)
        transforms.push(`rotateZ(${props.rotateZ}deg)`);
    return transforms.length > 0 ? transforms.join(" ") : "none";
};

const getCubicBezierEasing = (ease: string): string => {
    const easings: { [key: string]: string } = {
        "power1.out": "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        "power2.out": "cubic-bezier(0.165, 0.84, 0.44, 1)",
        "power3.out": "cubic-bezier(0.215, 0.61, 0.355, 1)",
        "power4.out": "cubic-bezier(0.23, 1, 0.32, 1)",
        "back.out": "cubic-bezier(0.175, 0.885, 0.32, 1.275)",
        "elastic.out": "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        "bounce.out": "cubic-bezier(0.68, -0.55, 0.265, 1.55)",
        ease: "ease",
        "ease-in": "ease-in",
        "ease-out": "ease-out",
        "ease-in-out": "ease-in-out",
        linear: "linear"
    };
    return easings[ease] || ease;
};

const AnimatedElement: React.FC<
    AnimatedElementProps & { className?: string; children: React.ReactNode }
> = ({ delay, duration, ease, from, to, isVisible, className, children }) => {
    const style: React.CSSProperties = {
        opacity: isVisible ? to.opacity : from.opacity,
        transform: isVisible ? getTransformValue(to) : getTransformValue(from),
        transition: `all ${duration}s ${getCubicBezierEasing(ease)} ${delay}s`,
        willChange: "transform, opacity"
    };

    return (
        <span
            className={cn("inline-block text-[36px]", className)}
            style={style}
        >
            {children}
        </span>
    );
};

const SplitContainer: React.FC<{
    textAlign: React.CSSProperties["textAlign"];
    className?: string;
    children: React.ReactNode;
}> = ({ textAlign, className, children }) => {
    return (
        <span
            className={cn(
                "inline-block overflow-hidden whitespace-normal break-words",
                className
            )}
            style={{ textAlign }}
        >
            {children}
        </span>
    );
};

const SplitLine: React.FC<{
    className?: string;
    children: React.ReactNode;
}> = ({ className, children }) => {
    return (
        <span className={cn("block overflow-hidden", className)}>
            {children}
        </span>
    );
};

const SplitWord: React.FC<{
    className?: string;
    children: React.ReactNode;
}> = ({ className, children }) => {
    return (
        <span className={cn("inline-block whitespace-nowrap", className)}>
            {children}
        </span>
    );
};

const AnimatedSplitText: React.FC<SplitTextProps> = ({
    text,
    className = "",
    delay = 100,
    duration = 0.6,
    ease = "power3.out",
    splitType = "chars",
    from = { opacity: 0, y: 40 },
    to = { opacity: 1, y: 0 },
    threshold = 0.1,
    rootMargin = "0px",
    textAlign = "center",
    tag = "p",
    onLetterAnimationComplete
}) => {
    const containerRef = useRef<HTMLElement>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [fontsLoaded, setFontsLoaded] = useState<boolean>(false);
    const hasTriggeredRef = useRef(false);

    // Normalize from/to props with defaults
    const normalizedFrom = useMemo(
        () => ({
            opacity: 0,
            y: 0,
            x: 0,
            scale: 1,
            rotateX: 0,
            rotateY: 0,
            rotateZ: 0,
            ...from
        }),
        [from]
    );

    const normalizedTo = useMemo(
        () => ({
            opacity: 1,
            y: 0,
            x: 0,
            scale: 1,
            rotateX: 0,
            rotateY: 0,
            rotateZ: 0,
            ...to
        }),
        [to]
    );

    useEffect(() => {
        const fallbackTimer = setTimeout(() => {
            setFontsLoaded(true);
        }, 100);

        if (typeof document === "undefined" || !document.fonts) {
            clearTimeout(fallbackTimer);
            setFontsLoaded(true);
            return;
        }

        if (document.fonts.status === "loaded") {
            clearTimeout(fallbackTimer);
            setFontsLoaded(true);
        } else {
            document.fonts.ready
                .then(() => {
                    clearTimeout(fallbackTimer);
                    setFontsLoaded(true);
                })
                .catch(() => {
                    clearTimeout(fallbackTimer);
                    setFontsLoaded(true);
                });
        }

        return () => clearTimeout(fallbackTimer);
    }, []);

    const splitText = useCallback(() => {
        if (!text || !fontsLoaded) return [];

        let elementIndex = 0;

        if (splitType === "lines") {
            const lines = text.split("\n");
            return lines.map((line, lineIndex) => (
                <SplitLine key={lineIndex} className="split-line">
                    {line.split("").map((char, charIndex) => {
                        const globalIndex = elementIndex++;
                        return (
                            <AnimatedElement
                                key={`${lineIndex}-${charIndex}`}
                                className="split-char"
                                delay={globalIndex * (delay / 1000)}
                                duration={duration}
                                ease={ease}
                                from={normalizedFrom}
                                to={normalizedTo}
                                isVisible={isVisible}
                            >
                                {char === " " ? "\u00A0" : char}
                            </AnimatedElement>
                        );
                    })}
                </SplitLine>
            ));
        }

        if (splitType.includes("words")) {
            const words = text.split(" ");

            if (splitType.includes("chars")) {
                return words.map((word, wordIndex) => {
                    const wordElements = word
                        .split("")
                        .map((char, charIndex) => {
                            const globalIndex = elementIndex++;
                            return (
                                <AnimatedElement
                                    key={`${wordIndex}-${charIndex}`}
                                    className="split-char"
                                    delay={globalIndex * (delay / 1000)}
                                    duration={duration}
                                    ease={ease}
                                    from={normalizedFrom}
                                    to={normalizedTo}
                                    isVisible={isVisible}
                                >
                                    {char}
                                </AnimatedElement>
                            );
                        });

                    return (
                        <React.Fragment key={wordIndex}>
                            <SplitWord className="split-word">
                                {wordElements}
                            </SplitWord>
                            {wordIndex < words.length - 1 && (
                                <AnimatedElement
                                    key={`space-${wordIndex}`}
                                    className="split-char"
                                    delay={elementIndex++ * (delay / 1000)}
                                    duration={duration}
                                    ease={ease}
                                    from={normalizedFrom}
                                    to={normalizedTo}
                                    isVisible={isVisible}
                                >
                                    {"\u00A0"}
                                </AnimatedElement>
                            )}
                        </React.Fragment>
                    );
                });
            } else {
                return words.map((word, index) => {
                    const globalIndex = elementIndex++;
                    return (
                        <React.Fragment key={index}>
                            <AnimatedElement
                                className="split-word"
                                delay={globalIndex * (delay / 1000)}
                                duration={duration}
                                ease={ease}
                                from={normalizedFrom}
                                to={normalizedTo}
                                isVisible={isVisible}
                            >
                                {word}
                            </AnimatedElement>
                            {index < words.length - 1 && (
                                <AnimatedElement
                                    key={`space-${index}`}
                                    className="split-char"
                                    delay={elementIndex++ * (delay / 1000)}
                                    duration={duration}
                                    ease={ease}
                                    from={normalizedFrom}
                                    to={normalizedTo}
                                    isVisible={isVisible}
                                >
                                    {"\u00A0"}
                                </AnimatedElement>
                            )}
                        </React.Fragment>
                    );
                });
            }
        }

        return text.split("").map((char, index) => (
            <AnimatedElement
                key={index}
                className="split-char"
                delay={index * (delay / 1000)}
                duration={duration}
                ease={ease}
                from={normalizedFrom}
                to={normalizedTo}
                isVisible={isVisible}
            >
                {char === " " ? "\u00A0" : char}
            </AnimatedElement>
        ));
    }, [
        text,
        splitType,
        delay,
        duration,
        ease,
        normalizedFrom,
        normalizedTo,
        isVisible,
        fontsLoaded
    ]);

    const splitElements = useMemo(() => splitText(), [splitText]);

    const triggerAnimation = useCallback(() => {
        if (hasTriggeredRef.current) return;

        hasTriggeredRef.current = true;
        setIsVisible(true);

        const totalElements = text.replace(/\s/g, "").length;
        const totalDuration = (totalElements - 1) * (delay / 1000) + duration;

        if (onLetterAnimationComplete) {
            setTimeout(onLetterAnimationComplete, totalDuration * 1000);
        }
    }, [text, delay, duration, onLetterAnimationComplete]);

    useEffect(() => {
        if (!containerRef.current || !fontsLoaded || splitElements.length === 0)
            return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry?.isIntersecting) {
                    triggerAnimation();
                }
            },
            {
                threshold,
                rootMargin
            }
        );

        const currentElement = containerRef.current;
        observer.observe(currentElement);

        const checkTimer = setTimeout(() => {
            if (!currentElement) return;
            const rect = currentElement.getBoundingClientRect();
            const isInView = rect.top < window.innerHeight && rect.bottom > 0;
            if (isInView) {
                triggerAnimation();
            }
        }, 50);

        return () => {
            observer.disconnect();
            clearTimeout(checkTimer);
        };
    }, [
        fontsLoaded,
        splitElements.length,
        threshold,
        rootMargin,
        triggerAnimation
    ]);

    const renderTag = () => {
        const classes = `split-parent ${className}`;

        const content = (
            <SplitContainer textAlign={textAlign} className={classes}>
                {splitElements}
            </SplitContainer>
        );

        switch (tag) {
            case "h1":
                return (
                    <h1
                        ref={
                            containerRef as React.RefObject<HTMLHeadingElement>
                        }
                    >
                        {content}
                    </h1>
                );
            case "h2":
                return (
                    <h2
                        ref={
                            containerRef as React.RefObject<HTMLHeadingElement>
                        }
                    >
                        {content}
                    </h2>
                );
            case "h3":
                return (
                    <h3
                        ref={
                            containerRef as React.RefObject<HTMLHeadingElement>
                        }
                    >
                        {content}
                    </h3>
                );
            case "h4":
                return (
                    <h4
                        ref={
                            containerRef as React.RefObject<HTMLHeadingElement>
                        }
                    >
                        {content}
                    </h4>
                );
            case "h5":
                return (
                    <h5
                        ref={
                            containerRef as React.RefObject<HTMLHeadingElement>
                        }
                    >
                        {content}
                    </h5>
                );
            case "h6":
                return (
                    <h6
                        ref={
                            containerRef as React.RefObject<HTMLHeadingElement>
                        }
                    >
                        {content}
                    </h6>
                );
            case "span":
                return (
                    <span
                        ref={containerRef as React.RefObject<HTMLSpanElement>}
                    >
                        {content}
                    </span>
                );
            default:
                return (
                    <p
                        ref={
                            containerRef as React.RefObject<HTMLParagraphElement>
                        }
                    >
                        {content}
                    </p>
                );
        }
    };

    return renderTag();
};

export default AnimatedSplitText;
